import React, { useRef, useEffect, useState } from 'react';
import { Send, Square, Loader2, Bot, User, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAIChatStore } from '@/stores/useAIChatStore';
import { sendAgentMessage, cancelAgentStream } from '@/services/ai-stream';
import AgentStepRenderer from './AgentStepRenderer';

interface AIChatPanelProps {
  projectId: string;
}

export default function AIChatPanel({ projectId }: AIChatPanelProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { messages, isStreaming, currentSteps, conversationId, error, clearChat, streamingText, currentPlan } = useAIChatStore();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentSteps, streamingText]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput('');
    sendAgentMessage(projectId, trimmed, conversationId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStop = async () => {
    // Cancel the client-side SSE stream immediately
    cancelAgentStream();

    // Also tell the server to abort the agent loop
    if (!conversationId) return;
    try {
      const token = localStorage.getItem('access_token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      await fetch(`${API_URL}/agent/${projectId}/conversations/${conversationId}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // ignore
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground border-b flex items-center justify-between">
        <span>AI Agent</span>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={clearChat}
              title="Clear chat"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Plan Card */}
      {currentPlan && (
        <div className="mx-3 mt-2 p-2 rounded-md bg-violet-500/5 border border-violet-500/20">
          <p className="text-xs font-semibold text-violet-400 mb-1">{currentPlan.title}</p>
          <div className="text-xs text-muted-foreground space-y-0.5">
            {currentPlan.markdown.split('\n').filter(Boolean).map((line, i) => {
              const stepText = line.replace(/^[-*]\s*/, '').replace(/^\d+\.\s*/, '');
              const normalizedStep = stepText.trim().toLowerCase();
              const isCompleted = currentPlan.completedSteps.some(
                (cs) => cs.trim().toLowerCase() === normalizedStep ||
                        normalizedStep.includes(cs.trim().toLowerCase()) ||
                        cs.trim().toLowerCase().includes(normalizedStep)
              );
              return (
                <div key={i} className="flex items-start gap-1.5">
                  {isCompleted ? (
                    <CheckCircle2 className="h-3 w-3 text-green-400 mt-0.5 shrink-0" />
                  ) : (
                    <div className="h-3 w-3 rounded-full border border-muted-foreground/30 mt-0.5 shrink-0" />
                  )}
                  <span className={isCompleted ? 'line-through opacity-60' : ''}>
                    {stepText}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {messages.length === 0 && !isStreaming && (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <Bot className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm font-medium">AI Agent</p>
            <p className="text-xs mt-1 max-w-[200px]">
              Describe what you want to build or change. The agent will plan, code, and execute.
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className="space-y-2">
            {/* Message header */}
            <div className="flex items-start gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                msg.role === 'user' ? 'bg-primary/10' : 'bg-violet-500/10'
              }`}>
                {msg.role === 'user' ? (
                  <User className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <Bot className="h-3.5 w-3.5 text-violet-500" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium mb-1">
                  {msg.role === 'user' ? 'You' : 'Agent'}
                </p>
                {/* Agent steps */}
                {msg.steps && msg.steps.length > 0 && (
                  <div className="mb-2">
                    <AgentStepRenderer steps={msg.steps} />
                  </div>
                )}
                {/* Message content */}
                {msg.content && (
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {msg.content}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Streaming indicator */}
        {isStreaming && (
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-violet-500/10">
                <Bot className="h-3.5 w-3.5 text-violet-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium mb-1">Agent</p>
                <AgentStepRenderer steps={currentSteps} />
                {/* Streaming text with blinking cursor */}
                {streamingText && (
                  <div className="text-sm whitespace-pre-wrap break-words mt-1">
                    {streamingText}
                    <span className="inline-block w-1.5 h-4 bg-violet-400 animate-pulse ml-0.5 align-text-bottom" />
                  </div>
                )}
                {currentSteps.length === 0 && !streamingText && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Processing...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-xs text-red-400 bg-red-500/10 rounded p-2">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-2">
        <div className="flex items-end gap-1.5">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask the agent to build or modify..."
            className="flex-1 resize-none bg-muted/50 rounded-md px-3 py-2 text-sm min-h-[36px] max-h-[120px] border-0 focus:outline-none focus:ring-1 focus:ring-primary"
            rows={1}
            disabled={isStreaming}
          />
          {isStreaming ? (
            <Button
              variant="destructive"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={handleStop}
              title="Stop"
            >
              <Square className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={handleSend}
              disabled={!input.trim()}
              title="Send"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
